import {
  Box,
  Button,
  CloseButton,
  createStyles,
  Flex,
  Group,
  Space,
  Stack,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import React from 'react';

import {
  convertWidgetFormToWidgetProps,
  ToggleWidget,
  ToggleWidgetForm,
  ToggleWidgetFormType,
} from '@portals/device-widgets';
import { useConfirmationModal } from '@portals/framework';
import { VerticalScrollBar } from '@portals/scrollbar';

import { CommonWidgetFormWrapperProps } from './form-wrappers.types';
import { useBooleanCommandOptions } from '../device-widgets.hooks';

export function ToggleWidgetFormWrapper({
  initialValues,
  onSubmit,
  onCancel,
  onClose,
  modelId,
  onAddCustomColor,
  colors,
}: CommonWidgetFormWrapperProps<ToggleWidgetFormType>) {
  const { classes } = useStyles();
  const asyncConfirmationCheck = useConfirmationModal();
  const form = useForm<ToggleWidgetFormType>({
    initialValues: {
      name: '',
      icon_name: 'battery_full',
      color: 'red.4',
      telemetry: '',
      command_name: '',
      command_param_key: '',
      title_telemetry: '',
      telemetry_as_title: false,
      ...(initialValues || {}),
    },
  });

  const { commandsOptions, paramsOptions } =
    useBooleanCommandOptions<ToggleWidgetFormType>({
      modelId,
      form,
      commandFieldName: 'command_name',
      commandParamFieldName: 'command_param_key',
    });

  const onCloseWrapper = async () => {
    if (!form.isDirty()) {
      onClose();
      return;
    }

    const isConfirmed = await asyncConfirmationCheck({
      title: 'Are you sure?',
      description: 'All your changes will be lost',
      confirmationLabel: 'Confirm',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) onClose();
  };

  const onCancelWrapper = async () => {
    if (!form.isDirty()) {
      onCancel();
      return;
    }

    const isConfirmed = await asyncConfirmationCheck({
      title: 'Are you sure?',
      description: 'All your changes will be lost',
      confirmationLabel: 'Confirm',
      cancelLabel: 'Cancel',
    });

    if (isConfirmed) onCancel();
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Box className={classes.container}>
        <Stack className={classes.preview} p={30} pr={0} pb={0} spacing={0}>
          <Text size={24} color="blue_gray.9">
            Add Toggle
          </Text>

          <VerticalScrollBar>
            <Space h="xl" />

            <ToggleWidgetForm
              form={form}
              commands={commandsOptions}
              params={paramsOptions}
              onAddCustomColor={onAddCustomColor}
              colors={colors}
            />

            <Space h="xl" />
          </VerticalScrollBar>
        </Stack>

        <Stack className={classes.config} p={30}>
          <Group position="right">
            <CloseButton size="xl" onClick={onCloseWrapper} />
          </Group>

          <Flex align="center" justify="center" h="100%">
            <ToggleWidget
              {...convertWidgetFormToWidgetProps.toggle({
                formValues: form.values,
              })}
              stackProps={{
                h: 179,
                w: 244,
              }}
            />
          </Flex>
        </Stack>

        <Group className={classes.footer} p="xl" position="right">
          <Button variant="default" onClick={onCancelWrapper}>
            {initialValues ? 'Close' : 'Back'}
          </Button>

          <Button
            type="submit"
            data-testid={initialValues ? 'save-widget-btn' : 'add-widget-btn'}
          >
            {initialValues ? 'Save' : 'Add Widget'}
          </Button>
        </Group>
      </Box>
    </form>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr min-content',
    gridTemplateAreas: `
      "preview config"
      "footer footer"
    `,
    gap: 0,
    height: 790,
  },
  preview: {
    gridArea: 'preview',
  },
  config: {
    gridArea: 'config',
    backgroundColor: theme.colors.gray[0],
  },
  footer: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    gridArea: 'footer',
  },
}));
